import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Menu, 
  Bell, 
  Search, 
  Settings, 
  HelpCircle, 
  Building2, 
  UserCircle, 
  Power, 
  Sparkles, 
  PlusCircle, 
  MinusCircle, 
  ExternalLink, 
  ShieldCheckIcon,
  UserCog
} from 'lucide-react';
import { FiMenu, FiBell, FiSearch, FiSettings, FiHelpCircle, FiHome } from 'react-icons/fi';
import { useAuth,  } from '../../context/AuthContext';
import { get } from '../../utils/apiUtils'; // Import the API utility
import { getCompanyData, setUserData } from '../../utils/authUtils';
import { upadateCompany } from '../../services/authService';
import { useToast } from '../common/ToastProvider';
import { useRefresh } from '../../context/RefreshContext';
import { downloadImage } from '../../services/fileUploadService';
import { useMsal } from '@azure/msal-react';
import { getCompaniesByFilter } from '../../services/companyService';

const basePath = process.env.PUBLIC_URL || "";


const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  background: #ffffff;
  // border-bottom: 1px solid var(--border-color);
  height: 50px;
  position: sticky;
  top: 0;
  z-index: 100;
  // box-shadow: 0 2px 4px rgba(0, 102, 255, 0.05);
`;
const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  margin-right: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
    color: #0066FF;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: #ffffff;
  color: var(--text-primary);
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #0066FF;
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
  font-size: 1rem;
`;

const CreateClaimButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem .5rem;
  margin-left: 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  margin-left: 0.5rem;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
    color: #0066FF;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--danger);
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
  }
`;

const Avatar = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 3px;
  background: #0066FF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  margin-right: 0.75rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

const UserInfo = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const UserRole = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const Dropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 102, 255, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04);
  min-width: 220px;
  overflow: hidden;
  z-index: 1000;
`;

const DropdownItem = styled.div`
  padding: 0.8rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 102, 255, 0.05);
  }
`;

const DropdownIcon = styled.div`
  font-size: 1.1rem;
  color: var(--text-secondary);
  font-family: 'Manrope', sans-serif;
`;

const DropdownText = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
`;

const BrandSection = styled.div`
  padding: 0 ${props => props.collapsed ? '0.5rem' : '1.2rem'};
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  gap: ${props => props.collapsed ? '0' : '0.75rem'};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(250, 251, 255, 0.9));
  backdrop-filter: blur(10px);
  // border-bottom: 1px solid #e5e9f2;
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left:4px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    // background: linear-gradient(90deg, transparent, rgba(0, 102, 255, 0.4), rgba(124, 58, 237, 0.3), transparent);
  }
`;
const BrandTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow: hidden;
`;

const BrandName = styled.div`
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--primary-500), var(--accent-500));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  transition: all 0.3s ease;
  
  ${BrandSection}:hover & {
    background: linear-gradient(135deg, var(--primary-600), var(--accent-600));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;
const BrandTagline = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  letter-spacing: 0.5px;
  color: var(--neutral-500);
  text-transform: uppercase;
  line-height: 1.2;
  transition: color 0.3s ease;
  
  ${BrandSection}:hover & {
    color: var(--primary-500);
  }
`;
const Submenu = styled.div`
  padding: 0.4rem 0.8rem;
  background: #ffffff;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
`;


const Logo = styled.img`
  height: 30px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;


const Header = ({ toggleSidebar, isSidebarCollapsed }) => {
  const navigate = useNavigate(); 
  const { refresh } = useRefresh();
  const { logout, user, setUser, setProfileUpdated, profileUpdated } = useAuth(); // Assuming setUser is available in AuthContext
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCompanies, setShowCompanies] = useState(false);
  const [companyData, setCompanyData] = useState({});
  const [hasNotifications, setHasNotifications] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [companyLogoImage, setCompanyLogoImage] = useState(null);

  // Fetch user data on component load
  const companies = getCompanyData();
  const { showToast } = useToast();
  const location = useLocation();
    const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await get(`/api/v1/mms_authentication/me`,{});
        setUser(response); // Update user context with fetched data
        setUserData(response)
        setProfileUpdated(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        // navigate('/login'); // Redirect to login if unauthorized
      }
    };

    fetchUserData();
  }, [navigate, setUser, profileUpdated]);

    useEffect(() => {
      if (user && user.companyCd) {
        const fetchCompany = async () => {
          try {
            const filter = { 
              code: user.companyCd
            };
            const response = await getCompaniesByFilter(filter);
            if (response.length) {
              const company = response[0];
              setCompanyData({
                id: company.id || '',
                name: company.name || '',
                status: company.status || '',
                description: company.description || '',
                logo_url: company.logo_url || '',
                currency: company.currency || '',
                date_format: company.date_format || '',
                timezone: company.timezone || ''
              });
            }
          } catch (err) {
            console.error("Error fetching company:", err);
          }
        };
  
        fetchCompany();
      }
    }, [user]);

  useEffect(() => {
    showUserDropdown && setShowUserDropdown(false);
  },[location])

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (user?.profile_url) {
        try {
          // const profile = await get(`${BASE_URL}/api/v1/mms_persistence/download${user.profile_url}`, {
          //   withCredentials: true,
          //   responseType: 'blob',
          // });
          
          const profile = await downloadImage(`${user.profile_url}`)
          const imageUrl = URL.createObjectURL(profile);
          setProfileImage(imageUrl);
        } catch (error) {
          console.error('Error fetching profile image:', error);
        }
      }
    };

    fetchProfileImage();
  }, [user?.profile_url]);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      if (companyData?.logo_url) {
        try {
          const logo = await downloadImage(`${companyData.logo_url}`);
          const logoUrl = URL.createObjectURL(logo);
          setCompanyLogoImage(logoUrl);
        } catch (error) {
          console.error('Error fetching company logo:', error);
        }
      }
    };

    fetchCompanyLogo();
  }, [companyData?.logo_url]);

  const handleLogout = async () => {
    setShowUserDropdown(false);
    if (process.env.REACT_APP_ENABLE_AZURE_LOGIN === "true" && accounts.length > 0) {
      instance.logoutRedirect({
        postLogoutRedirectUri: "/login",
      });
    } else {
      await logout();
      navigate('/login');
    }
  };

  const gotoBeta = () => {
    setShowUserDropdown(false);
    navigate('/manage')
    // window.open("https://www.figma.com/proto/eoJeOEnKEMIYKsOj2ZH50T/BlueMorMon?page-id=0%3A1&node-id=337-4567&viewport=-7691%2C34%2C1.01&t=fxtxhalFsiVzFI54-1&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=49%3A1515", "_blank", "noopener,noreferrer");
  };

  const handleCompanyChange = async (companyData) => {
    try {
      const res = await upadateCompany(companyData);
      if (res) {
        showToast({
          message: "Company changed successfully.",
          type: "success"
        });
        setShowUserDropdown(false);
        setShowCompanies(false);
        setUser(res);
        refresh()
      }
    } catch (error) {
      console.log("Error while changing the company.");
    }
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.firstname) return 'U';
    return [user.firstname.trim(), user.lastname.trim()].map(n => n[0]).join('').toUpperCase();
  };
  
  const hasAdminAccess = (roles) => {
    return roles?.includes('ROLE_ADMIN');
  }

  return (
    <HeaderContainer>
      <LeftSection>
        <MenuButton onClick={toggleSidebar} $isOpen={!isSidebarCollapsed}>
          <Menu size={20} strokeWidth={2} />
        </MenuButton>
        {companyLogoImage ? (
          <img 
            src={companyLogoImage} 
            alt="Company Logo"
            style={{ paddingLeft: '1rem',marginLeft: '1rem', marginLeft: '1rem', height: '35px', borderLeft: '1px solid rgba(0, 102, 255, 0.1)' }}
          />
          ):(
          <img src={`${basePath}/images/logo/meclaims-icon.png`} height={'45px'} />
        )}
        {/* <BrandTextContainer>
          <BrandName>BlueMorMon.ai</BrandName>
          <BrandTagline>The Agentic Platform</BrandTagline>
        </BrandTextContainer> */}
        {/* <SearchContainer>
          <SearchInput placeholder="Search..." />
          <SearchIconWrapper>
            <Search size={18} strokeWidth={2} />
          </SearchIconWrapper>
        </SearchContainer> */}
      </LeftSection>

      <RightSection>
        {/* {companyData.logo_url && (
          <img 
            src={companyData.logo_url} 
            alt="Company Logo"
            style={{ paddingRight: '1rem',marginLeft: '1rem', marginRight: '1rem', height: '25px', borderRight: '1px solid rgba(0, 102, 255, 0.1)' }}
          />
        )} */}
        {hasAdminAccess(user?.roles) && 
        <IconButton onClick={() => navigate('/company')}>
          <Building2 size={20} strokeWidth={2} />
        </IconButton>}


        {/* {hasAdminAccess(user?.roles) && 
        <IconButton onClick={() => navigate('/manage')}>
          <UserCog size={20} strokeWidth={2} />
        </IconButton>} */}
        {/* <IconButton>
          <HelpCircle size={20} strokeWidth={2} />
        </IconButton>
        <IconButton onClick={() => navigate('/manage')}>
          <Settings size={20} strokeWidth={2} />
        </IconButton> */}
        
        <IconButton>
          <Bell size={20} strokeWidth={2} />
          {hasNotifications && <NotificationBadge />}
        </IconButton>

        <UserProfile className='user-menu' onClick={() => setShowUserDropdown(!showUserDropdown)}>
          <Avatar>
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  objectFit: 'cover',
                }}
              />
            ) : (
              getUserInitials()
            )}
          </Avatar>
          <UserInfo>
            <UserName>{user?.firstname} {user?.lastname}</UserName>
            <UserRole>{user?.company_name || 'NA'}</UserRole>
          </UserInfo>
        </UserProfile>

        {showUserDropdown && (
          <Dropdown
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <DropdownItem onClick={() => navigate('/settings')}>
              <DropdownIcon><UserCircle size={18} strokeWidth={2} /></DropdownIcon>
              <DropdownText>Profile</DropdownText>
            </DropdownItem>
            <DropdownItem>
              <DropdownIcon><Sparkles size={18} strokeWidth={2} /></DropdownIcon>
              <DropdownText>Preferences</DropdownText>
            </DropdownItem>
            <DropdownItem 
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} 
                onClick={() => setShowCompanies(prev => !prev)}
              >
              <DropdownIcon><Building2 size={18} strokeWidth={2} /></DropdownIcon>

              <DropdownText>Company</DropdownText>
              {!showCompanies && <DropdownIcon style={{ marginLeft: 'auto' }}>
                <PlusCircle size={16} strokeWidth={2} />
              </DropdownIcon>}
              {showCompanies && <DropdownIcon style={{ marginLeft: 'auto' }}>
                <MinusCircle size={16} strokeWidth={2} />
              </DropdownIcon>}
            </DropdownItem>
            <Submenu isVisible={showCompanies}>
              {Array.isArray(companies) && companies.length > 0 ? (
                companies.map((company) => (
                  <DropdownItem key={company.company_cd} onClick={() => handleCompanyChange(company)}>
                    <DropdownText>{company.company_name}</DropdownText>
                  </DropdownItem>
                ))
              ) : (
                <DropdownItem>
                  <DropdownText>No companies available</DropdownText>
                </DropdownItem>
              )}
            </Submenu>
            {(user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ROLE_COMPANY_ADMIN')) && (
              <DropdownItem onClick={gotoBeta}>
                <DropdownIcon><ExternalLink size={18} strokeWidth={2} /></DropdownIcon>
                <DropdownText>Manage Company</DropdownText>
              </DropdownItem>
            )}
            <DropdownItem onClick={handleLogout}>
              <DropdownIcon><Power size={18} strokeWidth={2} /></DropdownIcon>
              <DropdownText>Logout</DropdownText>
            </DropdownItem>
          </Dropdown>
        )}
      </RightSection>
    </HeaderContainer>
  );
};

export default Header;
